import { createApp, defineComponent, ref, h } from 'vue';

/**
 * Programmatic confirm dialog using Bootstrap 5 styles.
 * Returns a Promise<boolean>.
 *
 * Usage:
 *   const ok = await confirm({ title: 'Löschen?', text: 'Wird dauerhaft gelöscht.', danger: true });
 *   if (!ok) return;
 */
export function confirm({ title, text, confirmText = 'Bestätigen', cancelText = 'Abbrechen', danger = false } = {}) {
  return new Promise((resolve) => {
    const mountEl = document.createElement('div');
    document.body.appendChild(mountEl);

    const cleanup = () => {
      app.unmount();
      if (document.body.contains(mountEl)) document.body.removeChild(mountEl);
    };

    const done = (result) => {
      visible.value = false;
      setTimeout(() => { cleanup(); resolve(result); }, 200);
    };

    const visible = ref(false);

    const ConfirmDialog = defineComponent({
      setup() {
        // Show after next tick so CSS transition plays
        setTimeout(() => { visible.value = true; }, 10);

        const onKeydown = (e) => { if (e.key === 'Escape') done(false); };
        document.addEventListener('keydown', onKeydown, { once: true });

        return () => {
          const btnConfirmClass = danger
            ? 'btn btn-sm px-4 fw-semibold text-white'
            : 'btn btn-sm btn-primary px-4 fw-semibold';

          const backdrop = h(
            'div',
            {
              class: 'modal-backdrop',
              style: {
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.45)',
                // Über allen App-Overlays (ISO-Usage-Modal 1400, Upload-Overlay 1500).
                zIndex: 2000,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                opacity: visible.value ? 1 : 0,
                transition: 'opacity 0.18s ease',
              },
            },
            [
              h(
                'div',
                {
                  style: {
                    background: 'var(--vz-secondary-bg, #fff)',
                    border: '1px solid var(--vz-border-color, #e9ebec)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                    width: '100%',
                    maxWidth: '400px',
                    margin: '0 1rem',
                    overflow: 'hidden',
                    transform: visible.value ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(12px)',
                    transition: 'transform 0.2s cubic-bezier(0.34,1.3,0.64,1)',
                  },
                  onClick: (e) => e.stopPropagation(),
                },
                [
                  // Colored top accent bar
                  danger
                    ? h('div', { style: { height: '4px', background: 'linear-gradient(90deg, #f46a6a, #ff8c8c)' } })
                    : h('div', { style: { height: '4px', background: 'linear-gradient(90deg, #405189, #0ab39c)' } }),

                  // Body
                  h('div', { style: { padding: '1.5rem 1.75rem 1.25rem' } }, [
                    h('h6', {
                      style: {
                        margin: '0 0 0.5rem',
                        fontWeight: 600,
                        fontSize: '1rem',
                        color: 'var(--vz-heading-color, #212529)',
                      },
                    }, title || 'Bestätigen'),
                    text
                      ? h('p', {
                          style: {
                            margin: '0 0 1.25rem',
                            fontSize: '0.875rem',
                            color: 'var(--vz-body-color, #6c757d)',
                            lineHeight: 1.5,
                          },
                        }, text)
                      : null,

                    // Buttons
                    h('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' } }, [
                      h('button', {
                        type: 'button',
                        class: 'btn btn-sm btn-light px-4',
                        onClick: () => done(false),
                      }, cancelText),
                      h('button', {
                        type: 'button',
                        class: btnConfirmClass,
                        style: danger ? { background: '#f46a6a', borderColor: '#f46a6a' } : {},
                        onClick: () => done(true),
                      }, confirmText),
                    ]),
                  ]),
                ]
              ),
            ]
          );

          return backdrop;
        };
      },
    });

    const app = createApp(ConfirmDialog);
    app.mount(mountEl);
  });
}
